import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import {
  trackGeoLockDialogShown,
  trackGeoLockConfirmed,
  trackGeoLockCancelled
} from '../../helpers';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: theme.spacing(2),
    maxWidth: 600
  },
  dialogTitle: {
    textAlign: 'center',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2)
  },
  warningIcon: {
    fontSize: 56,
    color: theme.palette.warning.main
  },
  titleText: {
    fontWeight: 700,
    fontSize: '1.5rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1)
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
    fontStyle: 'italic'
  },
  dialogContent: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4)
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1.1rem',
    marginBottom: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  impactBox: {
    backgroundColor: theme.palette.error.light + '15',
    border: `2px solid ${theme.palette.error.light}`,
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(3),
    position: 'relative'
  },
  impactText: {
    fontSize: '1rem',
    lineHeight: 1.6,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1)
  },
  impactHighlight: {
    fontWeight: 700,
    color: theme.palette.error.main,
    fontSize: '1.1rem'
  },
  benefitsBox: {
    backgroundColor: theme.palette.success.light + '15',
    border: `2px solid ${theme.palette.success.light}`,
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2)
  },
  benefitsList: {
    padding: 0
  },
  benefitItem: {
    padding: theme.spacing(0.5, 0),
    alignItems: 'flex-start'
  },
  benefitIcon: {
    color: theme.palette.success.main,
    minWidth: 36,
    marginTop: 4
  },
  benefitText: {
    '& .MuiListItemText-primary': {
      fontSize: '0.95rem',
      lineHeight: 1.5,
      color: theme.palette.text.primary
    }
  },
  recommendationBox: {
    backgroundColor: theme.palette.info.light + '15',
    border: `2px dashed ${theme.palette.info.main}`,
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  recommendationText: {
    fontSize: '0.9rem',
    lineHeight: 1.6,
    color: theme.palette.text.secondary,
    fontStyle: 'italic'
  },
  recommendationStrong: {
    fontWeight: 600,
    color: theme.palette.info.dark
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  dialogActions: {
    padding: theme.spacing(2, 3, 3, 3),
    justifyContent: 'space-between',
    gap: theme.spacing(2)
  },
  cancelButton: {
    minWidth: 140,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '1rem',
    padding: theme.spacing(1.5, 3),
    color: theme.palette.text.secondary,
    borderColor: theme.palette.divider,
    '&:hover': {
      borderColor: theme.palette.text.secondary,
      backgroundColor: theme.palette.action.hover
    }
  },
  confirmButton: {
    minWidth: 200,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '1rem',
    padding: theme.spacing(1.5, 3),
    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
    color: theme.palette.common.white,
    boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
    '&:hover': {
      background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
      boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
      transform: 'translateY(-1px)'
    },
    transition: 'all 0.2s ease'
  },
  trustBadge: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(1)
  },
  trustText: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5)
  },
  lockIcon: {
    fontSize: '1rem',
    color: theme.palette.success.main
  }
}));

/**
 * GeoLockConfirmationDialog Component
 *
 * Displays a confirmation dialog when user selects the geo-lock plan.
 * Provides transparent information about trade-offs while building trust.
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onConfirm - Callback when user confirms
 * @param {function} onCancel - Callback when user cancels
 * @param {string} selectedRegions - The regions selected for restriction
 */
const GeoLockConfirmationDialog = ({
  open,
  onConfirm,
  onCancel,
  selectedRegions,
  jobId = ''
}) => {
  const classes = useStyles();

  // Track when dialog is shown
  useEffect(() => {
    if (open) {
      trackGeoLockDialogShown(jobId, selectedRegions);
    }
  }, [open, jobId, selectedRegions]);

  const handleConfirm = () => {
    trackGeoLockConfirmed(jobId, selectedRegions);
    onConfirm();
  };

  const handleCancel = () => {
    trackGeoLockCancelled(jobId);
    onCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
      aria-labelledby="geolock-confirmation-title"
      aria-describedby="geolock-confirmation-description">
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Box className={classes.iconContainer}>
          <LocationOnIcon className={classes.warningIcon} />
        </Box>
        <Typography variant="h4" className={classes.titleText}>
          Enable Geographic Restrictions?
        </Typography>
        <Typography className={classes.subtitle}>
          Important information about geo-locking your job post
        </Typography>
      </DialogTitle>

      <DialogContent
        className={classes.dialogContent}
        id="geolock-confirmation-description">
        {/* Impact Warning */}
        <Box className={classes.impactBox}>
          <Typography className={classes.sectionTitle}>
            <TrendingDownIcon color="error" />
            What to Expect
          </Typography>
          <Typography className={classes.impactText}>
            Geo-locked posts receive{' '}
            <span className={classes.impactHighlight}>
              significantly fewer views
            </span>{' '}
            (typically 60-90% less) because they're only visible to candidates
            in your specified regions.
          </Typography>
          <Typography
            className={classes.impactText}
            style={{ marginBottom: 0 }}>
            <strong>Selected region:</strong>{' '}
            {selectedRegions || 'Not specified'}
          </Typography>
        </Box>

        {/* Benefits */}
        <Box className={classes.benefitsBox}>
          <Typography className={classes.sectionTitle}>
            <TrendingUpIcon color="success" />
            Why Use Geo-Locking?
          </Typography>
          <List className={classes.benefitsList} dense>
            <ListItem className={classes.benefitItem}>
              <ListItemIcon className={classes.benefitIcon}>
                <CheckCircleIcon />
              </ListItemIcon>
              <ListItemText
                primary="Higher quality applicants - Only candidates who can legally work in your region"
                className={classes.benefitText}
              />
            </ListItem>
            <ListItem className={classes.benefitItem}>
              <ListItemIcon className={classes.benefitIcon}>
                <CheckCircleIcon />
              </ListItemIcon>
              <ListItemText
                primary="Better conversion rates - Fewer unqualified applications to review"
                className={classes.benefitText}
              />
            </ListItem>
            <ListItem className={classes.benefitItem}>
              <ListItemIcon className={classes.benefitIcon}>
                <CheckCircleIcon />
              </ListItemIcon>
              <ListItemText
                primary="Compliance assurance - Meet location-specific hiring requirements automatically"
                className={classes.benefitText}
              />
            </ListItem>
            <ListItem className={classes.benefitItem}>
              <ListItemIcon className={classes.benefitIcon}>
                <CheckCircleIcon />
              </ListItemIcon>
              <ListItemText
                primary="Time savings - No need to manually filter out international applicants"
                className={classes.benefitText}
              />
            </ListItem>
          </List>
        </Box>

        {/* Recommendation */}
        <Box className={classes.recommendationBox}>
          <Typography className={classes.recommendationText}>
            <span className={classes.recommendationStrong}>
              <span role="img" aria-label="light bulb">💡</span> Our Recommendation:
            </span>{' '}
            Geo-locking works best for roles with specific location requirements
            (visa restrictions, in-person requirements, local labor laws). For
            fully remote roles open to global talent, we recommend leaving it
            disabled for maximum reach.
          </Typography>
        </Box>

        <Divider className={classes.divider} />

        {/* Trust Badge */}
        <Box className={classes.trustBadge}>
          <Typography className={classes.trustText}>
            <span className={classes.lockIcon} role="img" aria-label="shield">🛡️</span>
            <strong>Secure & Private:</strong> Location detection is transparent
            and never stores personal data
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          className={classes.cancelButton}
          startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          className={classes.confirmButton}
          startIcon={<CheckCircleIcon />}
          autoFocus>
          Yes, Enable Geo-Lock
        </Button>
      </DialogActions>
    </Dialog>
  );
};

GeoLockConfirmationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  selectedRegions: PropTypes.string,
  jobId: PropTypes.string
};

GeoLockConfirmationDialog.defaultProps = {
  selectedRegions: 'Not specified'
};

export default GeoLockConfirmationDialog;
